import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import ProjectConfig from 'ProjectConfig';
import EnvironmentManager from 'Utils/environmentManager';
import { getUserTheme, normalizeTheme, subscribeUserTheme, THEMES } from 'Utils/theme';

const CMS_KEY_TURNSTILE_CAPTCHA = 'cdn_turnstile_captcha';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadTurnstileScript (siteKey, onReady) {
  if (typeof window === 'undefined') return;
  if (window.turnstile) {
    window.TURNSTILE_SITE_KEY = siteKey;
    onReady();
    return;
  }
  const script = document.createElement('script');
  script.setAttribute('data-cfasync', 'false');
  script.async = false;
  script.defer = false;
  script.src = TURNSTILE_SCRIPT_URL;
  script.onload = () => {
    window.TURNSTILE_SITE_KEY = siteKey;
    onReady();
  };
  script.onerror = () => {};
  document.head.appendChild(script);
}

const TurnstileWidget = forwardRef(function TurnstileWidget ({ onToken, onEnabled, size, className }, ref) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [theme, setTheme] = useState(getUserTheme);
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const propsRef = useRef({ onToken, onEnabled, size });
  propsRef.current = { onToken, onEnabled, size };

  useEffect(() => subscribeUserTheme(setTheme), []);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (typeof window !== 'undefined' && window.turnstile && widgetIdRef.current != null) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch (e) { /* no-op */ }
      }
      propsRef.current.onToken('');
    }
  }), []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let isCancelled = false;

    EnvironmentManager.waitForMessages().then(() => {
      if (isCancelled) return;
      if (window.location.hostname === 'localhost') return;
      const value = (EnvironmentManager.getMessageText({ key: CMS_KEY_TURNSTILE_CAPTCHA }) || '').trim().toLowerCase();
      if (value !== 'true') return;
      const siteKey = ProjectConfig.TURNSTILE_SITE_KEY || '';
      if (!siteKey) return;
      loadTurnstileScript(siteKey, () => {
        if (!isCancelled) setIsEnabled(true);
      });
    });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEnabled) return undefined;

    let timer = null;

    const renderWidget = () => {
      if (!containerRef.current || widgetIdRef.current != null) return;
      if (!window.turnstile || !window.TURNSTILE_SITE_KEY) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: window.TURNSTILE_SITE_KEY,
        theme: normalizeTheme(theme) === THEMES.DARK ? 'dark' : 'light',
        callback: token => propsRef.current.onToken(token),
        'error-callback': errorCode => {
          console.warn('[Turnstile] Challenge error:', errorCode);
          propsRef.current.onToken('');
        },
        'expired-callback': () => propsRef.current.onToken(''),
        retry: 'auto',
        'retry-interval': 3000,
        ...(propsRef.current.size ? { size: propsRef.current.size } : {})
      });
    };

    propsRef.current.onEnabled?.();

    if (window.turnstile) {
      window.turnstile.ready(() => {
        timer = setTimeout(renderWidget, 100);
      });
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (window.turnstile && widgetIdRef.current != null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) { /* no-op */ }
        widgetIdRef.current = null;
      }
      propsRef.current.onToken('');
    };
  }, [isEnabled, theme]);

  if (!isEnabled) return null;

  return (
    <div
      ref={ containerRef }
      className={ className }
      aria-label="Güvenlik doğrulaması"
    />
  );
});

TurnstileWidget.propTypes = {
  onToken: PropTypes.func.isRequired,
  onEnabled: PropTypes.func,
  size: PropTypes.string,
  className: PropTypes.string
};

export default TurnstileWidget;
